/** Result returned by the commit gate after a seek attempt. */
export interface SeekResult {
  /** Whether this seek landed within the deadband and committed a real decoded frame. */
  status: "committed" | "superseded" | "timed-out" | "out-of-tolerance";
  /** The actual `video.currentTime` after settling (regardless of status). */
  finalTime: number;
}

export interface SeekOptions {
  /** Acceptable distance from target in seconds. Default: 0.08 */
  deadbandSec?: number;
  /** Fallback timeout in ms. A timeout is NOT success — surfaces as "timed-out". Default: 500 */
  timeoutMs?: number;
  /** Maximum additional retries after the first attempt on out-of-tolerance. Default: 2 */
  maxRetries?: number;
  /**
   * When this returns true the seek is abandoned without committing.
   * Use this for latest-wins epoch semantics: return `epoch !== latestEpoch`.
   */
  supersededSignal?: () => boolean;
}

/**
 * Seek a video element to `timeSec` and wait for a committed decoded frame.
 *
 * Improvements over the prior timeout-as-success approach:
 * - Awaits the `seeked` event, then verifies the landed position is within `deadbandSec`.
 * - Retries up to `maxRetries` times on out-of-tolerance landings.
 * - A timeout resolves as "timed-out" (not committed), never as success.
 * - Respects `supersededSignal` so callers can implement latest-wins seek semantics.
 */
export async function seekVideoWithGate(
  video: HTMLVideoElement,
  timeSec: number,
  options: SeekOptions = {},
): Promise<SeekResult> {
  const target = Math.max(0, timeSec);
  const deadband = options.deadbandSec ?? 0.08;
  const timeoutMs = options.timeoutMs ?? 500;
  const maxRetries = options.maxRetries ?? 2;
  const isSuperseded = options.supersededSignal ?? (() => false);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (isSuperseded()) {
      return { status: "superseded", finalTime: video.currentTime };
    }

    // Fast path: already on target and not actively seeking.
    if (Math.abs(video.currentTime - target) <= deadband && !video.seeking) {
      return { status: "committed", finalTime: video.currentTime };
    }

    // Assign `currentTime` and await the `seeked` event with a hard timeout.
    const settled = await new Promise<"seeked" | "timed-out">((resolve) => {
      let done = false;
      const finish = (result: "seeked" | "timed-out") => {
        if (done) return;
        done = true;
        video.removeEventListener("seeked", onSeeked);
        clearTimeout(timer);
        resolve(result);
      };
      const onSeeked = () => finish("seeked");
      const timer = setTimeout(() => finish("timed-out"), timeoutMs);
      video.addEventListener("seeked", onSeeked);
      video.currentTime = target;
    });

    // Re-check superseded now that we've awaited.
    if (isSuperseded()) {
      return { status: "superseded", finalTime: video.currentTime };
    }

    if (settled === "timed-out") {
      // Timeout is NOT success; surface it so callers can decide.
      return { status: "timed-out", finalTime: video.currentTime };
    }

    // seeked fired — verify the landed position is within the deadband.
    if (Math.abs(video.currentTime - target) <= deadband) {
      return { status: "committed", finalTime: video.currentTime };
    }

    // Out of tolerance — will retry if attempts remain (loop continues).
  }

  return { status: "out-of-tolerance", finalTime: video.currentTime };
}
